import React from 'react';

import { theme } from 'antd';
import dayjs from 'dayjs';
import { Calendar, Newspaper } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { DashboardConfig } from 'models/DashboardConfig';
import { DashboardNew } from 'models/DashboardNew';

import appI18n from '../../../config/i18n';
import DetailCarouselModal from './DetailCarouselModal';

import './NewsGrid.scss';

interface NewsGridProps {
  items: DashboardNew[];
  loading?: boolean;
}

const NewsGrid: React.FC<NewsGridProps> = ({ items, loading = false }) => {
  const [selectedItem, setSelectedItem] = React.useState<DashboardNew | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const { token } = theme.useToken();
  const dashboardConfig: DashboardConfig = useSelector(
    (state: any) => state.dashboardConfig
  );
  const primaryColor: string =
    dashboardConfig?.primaryColor || token.colorPrimary;
  const dateFormat: string = useSelector(
    (state: any) => state.intl?.dateFormat || 'DD/MM/YYYY'
  );

  const showModal = React.useCallback((item: DashboardNew) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  }, []);

  const closeModal = React.useCallback(() => {
    setSelectedItem(null);
    setIsModalOpen(false);
  }, []);

  // Memoizar las cards para no re-renderizarlas cuando solo cambia el idioma
  const cards = React.useMemo(() => {
    if (loading) {
      return [...Array(6)].map((_, index) => (
        <div key={index} className="chome-news-card chome-skeleton">
          <div className="chome-news-image-container chome-skeleton-image" />
          <div className="chome-skeleton-title" />
          <div className="chome-skeleton-date" />
        </div>
      ));
    }

    if (items.length === 0) return null;

    return items.map((item, idx) => (
      <div
        key={item.id}
        className="chome-news-card"
        onClick={() => showModal(item)}
      >
        <div className="chome-news-image-container">
          <img
            src={item.logoUrl}
            alt={item.title}
            className="chome-news-image"
            decoding="async"
            loading={idx === 0 ? 'eager' : 'lazy'}
            // fetchPriority no está tipado en @types/react 18.2.x;
            // lo pasamos como atributo HTML literal en minúsculas.
            {...({
              fetchpriority: idx === 0 ? 'high' : 'auto',
            } as Record<string, string>)}
          />
        </div>
        <h3 className="chome-news-title">{item.title}</h3>
        <p className="chome-news-date">
          <Calendar size={14} className="chome-date-icon" />
          {dayjs(item.publishedDate).format(dateFormat)}
        </p>
      </div>
    ));
    // dateFormat y showModal son estables — no cambian con el idioma
  }, [items, loading, dateFormat]);

  const emptyState = items.length === 0 && !loading && (
    <div className="chome-empty-state">
      <Newspaper size={48} />
      <h3>{t('common.news_grid.empty.title')}</h3>
      <p>{t('common.news_grid.empty.subtitle')}</p>
    </div>
  );

  return (
    <div
      className="chome-news-wrapper"
      style={{ backgroundColor: `${primaryColor}1a` }}
    >
      {/* Header row */}
      <div className="chome-news-header">
        <div
          className="chome-news-header-icon"
          style={{ backgroundColor: primaryColor }}
        >
          <Newspaper size={20} color="#fff" />
        </div>
        <span
          className="chome-news-header-title"
          style={{ color: token.colorText }}
        >
          {t('common.news_grid.title')}
        </span>
      </div>

      {/* Cards grid */}
      <div className="chome-news-grid">{emptyState || cards}</div>

      {/* Modal */}
      {selectedItem && (
        <DetailCarouselModal
          isOpen={isModalOpen}
          handleModalCancel={closeModal}
          item={selectedItem}
        />
      )}
    </div>
  );
};

export default NewsGrid;
